import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCreateInterviewType,
  useGetOrgLogo,
  useListExtractions,
  useListRubrics,
  getListInterviewTypesQueryKey,
  type ExtractionListItemDto,
  type RubricListItemDto,
} from '@sara/api-client';
import {
  CreateInterviewTypeForm,
  type CreateInterviewTypeFormValues,
} from '../../components/CreateInterviewTypeForm';
import { toast } from '@sara/ui/components/sonner';
import { SHOW_ORG_LOGO_TO_API } from '../../lib/show-org-logo';
import { consentConfigToApi } from '../../lib/consent-fields';
import { useOrgSlug } from '../../../auth';
import { getApiErrorMessage } from '../../../../lib/api-error';
import type { CreateInterviewTypeFormContainerProps } from './types';

export function CreateInterviewTypeFormContainerContent({
  orgId,
}: CreateInterviewTypeFormContainerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgSlug = useOrgSlug();

  const { data: logo } = useGetOrgLogo({ orgId });
  // While the query is loading (`logo` is undefined) treat the org as having a
  // logo so the negative upload hint isn't shown prematurely; once resolved it
  // reflects the real `logoUrl`.
  const orgHasLogo = logo === undefined || logo.logoUrl !== null;

  const { data: extractionsData } = useListExtractions({ orgId });
  const { data: rubricsData } = useListRubrics({ orgId });

  const extractions = (extractionsData ?? [])
    .filter((e: ExtractionListItemDto) => e.archivedAt === null)
    .map((e: ExtractionListItemDto) => ({
      id: e.id,
      name: e.name,
      latestVersionId: e.latestVersionId,
      latestVersion: e.latestVersion,
    }));

  const rubrics = (rubricsData ?? [])
    .filter((r: RubricListItemDto) => r.archivedAt === null)
    .map((r: RubricListItemDto) => ({
      id: r.id,
      name: r.name,
      latestVersionId: r.latestVersionId,
      latestVersion: r.latestVersion,
    }));

  const { mutate, isPending, error } = useCreateInterviewType({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getListInterviewTypesQueryKey(),
        });
        void navigate({ to: '/orgs/$orgSlug/templates', params: { orgSlug } });
      },
      onError: (err) => {
        toast.error('Failed to create template', { description: getApiErrorMessage(err) });
      },
    },
  });

  function handleSubmit(values: CreateInterviewTypeFormValues) {
    mutate({
      data: {
        orgId,
        name: values.name,
        description: values.description,
        // Strict 1:1: the API always duplicates the base agent for this template
        // and writes this prompt + first message onto the fresh copy. The system
        // prompt is required (validated by the form), so it's always authored.
        agentConfig: { systemPrompt: values.systemPrompt, firstMessage: values.firstMessage },
        initialVersion: {
          // The system prompt lives on the ElevenLabs agent, not the version.
          agentPrompt: '',
          extractionVersionId:
            values.extractionVersionId && values.extractionVersionId !== 'none'
              ? values.extractionVersionId
              : undefined,
          rubricVersionId:
            values.rubricVersionId && values.rubricVersionId !== 'none'
              ? values.rubricVersionId
              : undefined,
          title: values.candidateTitle,
          description: values.candidateDescription,
          endOfCallNotes: values.candidateEndOfCallNotes,
          declinedMessage: values.candidateDeclinedMessage,
          showOrgLogo: SHOW_ORG_LOGO_TO_API[values.candidateShowOrgLogo],
          ...consentConfigToApi(values.consentConfig),
        },
      },
    });
  }

  return (
    <div className="space-y-6">
      {error ? (
        <div className="whitespace-pre-line rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {getApiErrorMessage(error) ?? 'Failed to create template'}
        </div>
      ) : null}
      <CreateInterviewTypeForm
        onSubmit={handleSubmit}
        onCancel={() => navigate({ to: '/orgs/$orgSlug/templates', params: { orgSlug } })}
        isPending={isPending}
        orgHasLogo={orgHasLogo}
        extractions={extractions}
        rubrics={rubrics}
      />
    </div>
  );
}
